import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ConfigPageComponent} from 'features/core/shared/mvs-page/config-page.component';
import {WidgetConfigInterface} from 'features/core/shared/mvs-page/interface/widget-config-page.interface';
import {NavigationItem} from 'features/core/shared/dto/navigation/navigation-item';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsObjectNavigationService} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {MvsObjectNavigationEntry} from 'features/core/shared/navigation/mvs-object-navigation-entry';
import {MvsObjectNavigationActionEnum} from 'features/core/shared/navigation/mvs-object-navigation-action-enum';
import {PageHeaderConfig} from 'features/core/shared/service/page/page-header-config.interface';
import {
    MvsConfigHeaderComponent
} from 'features/core/shared/components/mvs-config-header/mvs-config-header.component';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from 'features/core/shared/helper/widget-function-call-back-create';

/**
* cn (Commission iNcome) configuration page.
*
* CONFIG-001/002/003: the SINGLE config route for the cn module (cn/config).
* Hosts every cn configuration entity behind one navigation sidebar:
*   - Provisionsmodelle (default)  selectable CommissionModel -> filtered CommissionRule
*   - Zuordnungen                  CommissionModelAssignment
*   - Bemessungsgrundlagen         CommissionCalculationBase (flat catalog)
*   - Caps                         CommissionCap (flat catalog)
*   - Datenqualität                data-quality panel
*
* Page header runs in Component-Mode via MvsConfigHeaderComponent (CONFIG-008).
* Complex configuration entities (CommissionModel, CommissionRule) are NEVER edited
* inline here; clicking a row navigates to a dedicated Object-Page (CONFIG-003/015).
*
* NOTE (architecture shell): widget instances and the selectable model -> rule flow
* (FR-2/FR-3, 3-state lifecycle, FK-defaulting, click-chain) are wired by the Data /
* Component specialist in FE-T2/FE-T3/FE-T4. This file establishes the page skeleton,
* inheritance contract, header and navigation structure only.
*/
@Component({
    selector: 'mvs-cn-config-page',
    templateUrl: './cn-config.page.html',
    styleUrls: ['./cn-config.page.scss'],
    standalone: false
})
export class CnConfigPage extends ConfigPageComponent implements OnInit {

    defaultLabel: string = 'Provisionseingang Konfiguration';

    activeNavItem: string;

    /**
    * Navigation sidebar items (CONFIG-006: exactly one default, CONFIG-017: fa-solid icons,
    * labels in German with proper umlauts).
    */
    navigationItems: NavigationItem[] = [
        {
            label: 'Provisionsmodelle',
            action: 'commissionModels',
            tooltip: 'Provisionsmodelle',
            icon: 'fa-solid fa-folder-tree',
            toggleable: false,
            default: true
        },
        {
            label: 'Zuordnungen',
            action: 'assignments',
            tooltip: 'Zuordnungen',
            icon: 'fa-solid fa-diagram-project',
            toggleable: false,
            default: false
        },
        {
            label: 'Bemessungsgrundlagen',
            action: 'calculationBases',
            tooltip: 'Bemessungsgrundlagen',
            icon: 'fa-solid fa-ruler',
            toggleable: false,
            default: false
        },
        {
            label: 'Caps',
            action: 'caps',
            tooltip: 'Caps',
            icon: 'fa-solid fa-gauge-high',
            toggleable: false,
            default: false
        },
        {
            label: 'Provisionspartner',
            action: 'commissionPartners',
            tooltip: 'Provisionspartner',
            icon: 'fa-solid fa-building',
            toggleable: false,
            default: false
        },
        {
            label: 'Datenqualität',
            action: 'dataQuality',
            tooltip: 'Datenqualität',
            icon: 'fa-solid fa-shield-check',
            toggleable: false,
            default: false
        },
    ];

    /**
    * Stable alias for the filtered CommissionRule detail widget. The SAME alias is reused across
    * the empty (no model selected) and filtered (model selected) states so the widget identity —
    * and any saved layout — is preserved (FR-3, 3-state lifecycle). Declared before `widgets`
    * because the declarative map references it in its initializer.
    */
    private static readonly RULE_ALIAS = 'cn.config.commission.rule.filtered.new';

    /** URL query param that persists the selected model id so a refresh keeps the filtered view (CONFIG-021). */
    private static readonly MODEL_ID_PARAM = 'modelId';

    /**
    * Declarative widgets (eagerly initialized by ConfigPageComponent.initializeWidgets()):
    *
    *   commissionModelWidget    selectable CommissionModel (left, col-3); showSearch + name display.
    *                            Selection drives the filtered rule list (master-detail).
    *   commissionRuleWidget     filtered CommissionRule (right, col-9). createFunction returns an
    *                            empty WidgetData so no unfiltered QL fires on page load — the real
    *                            widget is (re)built lazily in postNavigationSelection() / on select
    *                            via refreshRuleWidget(), reusing the SAME alias across every state.
    *   commissionModelAssignmentWidget  flat CommissionModelAssignment catalog.
    *   calculationBaseWidget    flat CommissionCalculationBase catalog.
    *   capWidget                flat CommissionCap catalog.
    */
    override widgets: { [key: string]: WidgetConfigInterface } = {
        // Provisionsmodelle — selectable master (CONFIG-011: showSearch on the parent selector).
        commissionModelWidget: {
            alias: 'cn.config.commission.model.selectable.new',
            label: 'Provisionsmodelle',
            entity: 'cn.CommissionModel',
            widgetData: new WidgetData(),
            postFunction: (widget: WidgetConfigInterface) => {
                widget.widgetData.uiComponent = 'selectable';
                widget.widgetData.setParam(WidgetDataParam.create('attributeToDisplay', 'e.name'));
                widget.widgetData.setParam(WidgetDataParam.create('showSearch', true));
            }
        },

        // Regeln — filtered detail. Empty placeholder on load; real widget built in refreshRuleWidget().
        commissionRuleWidget: {
            alias: CnConfigPage.RULE_ALIAS,
            label: 'Regeln',
            entity: 'cn.CommissionRule',
            widgetData: new WidgetData(),
            createFunction: () => new WidgetData()
        },

        // Zuordnungen — flat catalog.
        commissionModelAssignmentWidget: {
            alias: 'cn.config.commission.model.assignment.catalog.new',
            label: 'Zuordnungen',
            entity: 'cn.CommissionModelAssignment',
            widgetData: new WidgetData()
        },

        // Bemessungsgrundlagen — flat catalog.
        calculationBaseWidget: {
            alias: 'cn.config.commission.calculation.base.catalog.new',
            label: 'Bemessungsgrundlagen',
            entity: 'cn.CommissionCalculationBase',
            widgetData: new WidgetData()
        },

        // Caps — flat catalog.
        capWidget: {
            alias: 'cn.config.commission.cap.catalog.new',
            label: 'Caps',
            entity: 'cn.CommissionCap',
            widgetData: new WidgetData()
        },

        // Provisionspartner — flat catalog.
        commissionPartnerWidget: {
            alias: 'cn.config.commission.partner.catalog.new',
            label: 'Provisionspartner',
            entity: 'cn.CommissionPartner',
            widgetData: new WidgetData()
        }
    };

    /** Currently selected CommissionModel id (drives the filtered rule list); null = show all rules. */
    selectedCommissionModelId: number | null = null;

    /** The (re)built filtered CommissionRule detail widget, bound on the right (col-9). */
    commissionRuleWidgetData: WidgetData;

    constructor(
        protected override route: ActivatedRoute,
        protected override coreService: MvsCoreService,
        protected navigationService: MvsObjectNavigationService,
        protected router: Router
    ) {
        super(route, coreService);
    }

    ngOnInit(): void {
        this.defaultLabel = 'Provisionseingang Konfiguration';
        super.ngOnInit();
        this.initialized = true;
    }

    override postNavigationSelection(): void {
        if (this.activeNavigationItem) {
            this.activeNavItem = this.activeNavigationItem.action;
        }

        // Provisionsmodelle: restore the selected model from the URL (CONFIG-021) so a refresh
        // keeps the filtered rule view; otherwise (re)build the rule widget in 'show all' state.
        if (this.activeNavItem === 'commissionModels') {
            const modelIdParam = this.route.snapshot.queryParamMap.get(CnConfigPage.MODEL_ID_PARAM);
            if (modelIdParam) {
                const modelId = +modelIdParam;
                if (this.selectedCommissionModelId !== modelId) {
                    this.selectedCommissionModelId = modelId;
                    this.refreshRuleWidget(modelId);
                }
            } else if (!this.commissionRuleWidgetData) {
                this.refreshRuleWidget(null);
            }
        }
    }

    /**
    * AI: Regeln — (re)builds the CommissionRule detail widget under the stable RULE_ALIAS.
    * When a model is selected it is filtered by e.commissionModelId and FK-defaults
    * commissionModelDtoId for newly created rules; otherwise it shows all rules. Row-click opens
    * the dedicated rule Object-Page (CONFIG-015), never inline.
    * @widget cn.config.commission.rule.filtered.new (ql, list)
    */
    refreshRuleWidget(modelId: number | null): void {
        const filters = modelId != null
            ? [FilterCriteria.create('e.commissionModelId', FilterCriteria.cOperatorEqual, modelId)]
            : [];

        this.commissionRuleWidgetData = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            CnConfigPage.RULE_ALIAS,
            modelId != null ? 'Regeln' : 'Alle Regeln',
            'cn.CommissionRule',
            filters,
            // TM-1000001915 (B2): sort by real entity field orderPriority (e.priority does not exist)
            [new Sorting('e.orderPriority', true)]
        );

        if (modelId != null) {
            const fields: WidgetToolbarCreateInterface[] = [{fieldName: 'commissionModelDtoId', fieldValue: modelId}];
            this.commissionRuleWidgetData.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);
        }
    }

    /**
    * Master selection: a CommissionModel row was selected — filter the rule list to that model,
    * persist the selection in the URL (CONFIG-021), and FK-default new rules to it.
    */
    onCommissionModelSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const data = event as ObjectIdentifierData;
        const modelId = data?.data?.['e.id'] ?? event?.objectId;
        if (modelId == null) {
            return;
        }
        this.selectedCommissionModelId = modelId;
        this.refreshRuleWidget(modelId);
        this.router.navigate([], {
            queryParams: {[CnConfigPage.MODEL_ID_PARAM]: modelId},
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    /**
    * Master deselection: revert the rule list to 'show all' and strip the URL param (CONFIG-021).
    */
    onCommissionModelDeselect(): void {
        this.selectedCommissionModelId = null;
        this.refreshRuleWidget(null);
        this.router.navigate([], {
            queryParams: {[CnConfigPage.MODEL_ID_PARAM]: null},
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    /**
    * Click-to-Object-Page navigation (CONFIG-015).
    * Opens the dedicated Object-Page for a selected entity row instead of inline editing.
    */
    navigateTo(object: ObjectIdentifierData | ObjectIdentifier): void {
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            object.objectType, object.objectId, 'object', null, null, null, MvsObjectNavigationActionEnum.edit
        );
        this.navigationService.navigateTo(entry, 'main');
    }

    isNavComponent(): NavigationItem[] {
        return this.navigationItems;
    }

    getPageHeaderConfig(): PageHeaderConfig {
        return {
            component: MvsConfigHeaderComponent,
            inputs: {
                header: 'Provisionseingang Konfiguration',
                categoryTypeMode: 'configuration',
                icon: 'fa-light fa-coins',
                // Cross-links so the operational P3 screens are reachable from the cn config hub.
                links: [
                    {label: 'Dashboard', icon: 'fa-light fa-gauge', url: 'cn/dashboard'},
                    {label: 'Abgleich', icon: 'fa-light fa-scale-balanced', url: 'cn/reconciliation'}
                ]
            }
        };
    }
}
